import React from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Card, H2, HStack, Separator, Text, ToggleTip, VStack } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink, useFeatureFlag } from '@betterment/shared-components';

import { HouseholdPortfolioControlsDocument } from './graphql/query.householdPortfolioControls.generated';

import URLBuilder from '../../../Shared/url-builder';

interface PortfolioControlsProps {
  householdId: string;
}

export const PortfolioControls: React.FC<PortfolioControlsProps> = ({ householdId }) => {
  const { enabled: isFeatureFlagEnabled, loading } = useFeatureFlag({
    splitName: 'retail.b4a_ax_advisor_sec_lending_enabled',
    context: 'PortfolioControls',
  });
  const { data } = useGraphQLQuery(HouseholdPortfolioControlsDocument, {
    variables: { id: householdId },
    skip: !isFeatureFlagEnabled,
  });
  if (loading || !isFeatureFlagEnabled) {
    return null;
  }

  if (!data) return <CardLoader />;
  if (data.household.__typename === 'RecordNotFoundResult') return null;

  const { members } = data.household;
  const hasMultipleUsers = members.length > 1;

  const portfolioControls = members.map(member => ({
    userName: member.commonFirstName,
    isTaxLossHarvestingEnabled: member.portfolioControls.taxLossHarvestingEnabled,
    isSecuritiesLendingEnabled: member.portfolioControls.securitiesLending?.enabled,
  }));

  const getStatusSummary = (isEnabled: (control: (typeof portfolioControls)[0]) => boolean): string => {
    const enabledUsers = portfolioControls.filter(isEnabled);
    const hasNoneEnabled = enabledUsers.length === 0;

    if (hasNoneEnabled) {
      return 'Disabled';
    }

    if (hasMultipleUsers) {
      const names = enabledUsers.map(u => u.userName.split(' ')[0]).join(' and ');
      return `Enabled for ${names}`;
    }

    return 'Enabled';
  };

  const tlhEnabled = getStatusSummary(control => control.isTaxLossHarvestingEnabled);
  const secLendingEnabled = getStatusSummary(control => control.isSecuritiesLendingEnabled);

  return (
    <Card css={{ height: '100%' }}>
      <Card.Header css={{ padding: 'space.300' }}>
        <H2 variant="title200">Portfolio controls</H2>
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content
        css={{
          paddingX: 'space.300',
          paddingTop: 'space.300',
          paddingBottom: 'space.400',
          display: 'flex',
          gap: 'space.300',
          flexDirection: 'column',
        }}
      >
        <HStack css={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
            <Text variant="text100" css={{ fontWeight: 'font-weight.semibold' }}>
              Tax loss harvesting
            </Text>
            <ToggleTip>
              <ToggleTip.Trigger iconName="InfoCircle" label="Tax loss harvesting" />
              <ToggleTip.Content css={{ maxWidth: '325px' }} align="center" side="top">
                <Text>
                  Betterment will automatically sell losing investments to help offset taxes on future gains. We look
                  for these opportunities regularly to help improve your clients' after-tax returns.
                </Text>
              </ToggleTip.Content>
            </ToggleTip>
          </HStack>
          <Text variant="text100">{tlhEnabled}</Text>
        </HStack>
        <Separator css={{ fullWidthWithInset: 'space.0' }} />
        <HStack css={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
            <Text variant="text100" css={{ fontWeight: 'font-weight.semibold' }}>
              Securities lending
            </Text>
            <ToggleTip>
              <ToggleTip.Trigger iconName="InfoCircle" label="Securities lending" />
              <ToggleTip.Content css={{ maxWidth: '325px' }} align="center" side="top">
                <Text>
                  Coming in 2026, enrolled clients can earn income by lending securities they own without selling or
                  giving up investment exposure. You can sell lent securities or opt clients out of the securities
                  lending program at any time.
                </Text>
              </ToggleTip.Content>
            </ToggleTip>
          </HStack>
          <VStack css={{ alignItems: 'end' }}>
            <Text variant="text100">{secLendingEnabled}</Text>
            <SambaAutoLink to={URLBuilder.getEditSecuritiesLendingPage.absolute({ householdId })}>Edit</SambaAutoLink>
          </VStack>
        </HStack>
      </Card.Content>
    </Card>
  );
};
